<template>
  <div class="app-box padding-top-20 padding-left-30 padding-right-30">
    <div class="page-title line">
      {{ $t('message.payee.addPayeeAccountInfo') }}
    </div>
    <div class="payee-layout">
      <div class="payee-left-margin payee-item">
        <div class="details-title  padding-bottom-20">
          {{ $t('message.payee.essentialInformation') }}
        </div>
        <validation-component ref="basicInfoForm"
                              :validation-condition="basicInfoCondition">
        </validation-component>
        <div class="padding-bottom-30" >
          <el-checkbox id="read" v-model="isRead">
            <span class="payee-agreement">{{ $t('message.payee.readAgree') }}</span>
          </el-checkbox>
          <el-button type="text" @click="openAgreement">{{ $t('message.payee.customerAgreement') }}</el-button>
        </div>
      </div>
      <div class="payee-item">
        <div class="details-title  padding-bottom-20">
          {{ $t('message.payee.payeeAccount') }}
        </div>
        <validation-component ref="bankInfoForm"
                              :validation-condition="bankInfoCondition">
        </validation-component>
        <div style="text-align: right">
          <el-button @click="onCancel">{{ $t('message.tableCommon.cancel') }}</el-button>
          <el-button @click="onSubmit" type="primary" :loading="isLoading">{{ $t('message.common.submit') }}</el-button>
        </div>
      </div>
    </div>
    <customer-agreement ref="agreement" @onAgreeClick="onCustomerAgree"></customer-agreement>
  </div>
</template>
<script>

import { GetCityList, GetProvinceList } from '@/api/common/provinces-cities';
import { ShowErrorMessage, ShowSuccessMessage, ShowWarningMessage } from '@/components/modal/tips/message-tips';
import { AllNumber, CARD_LENGTH, ID_CARD, REQUIRED, STRING_LENGTH_256 } from '@/components/validation/tool/validator';
import ValidationComponent from '@/components/validation/validation';
import { initSelectedData } from '@/utils/tools/constant';
import { formatDateDisplay } from '@/utils/tools/time';
import { shallowClones } from '@/utils/tools/utils';
import PayeeManageService from '@/views/settlement/api/settlement-payee';
import CustomerAgreement from '@/views/settlement/payee-manage/agreement/agreement.vue';
import { GenderStatus, ProfessionStatus } from '@/views/settlement/payee-manage/status/payee';

export default {
  components: {
    ValidationComponent,
    CustomerAgreement,
  },
  data() {
    return {
      ...PayeeManageService,
      isLoading: false,
      isRead: false,
      infoData: {},
      cardId: null,
      basicInfoCondition: [
        {
          key: 'upload_image',
          name: 'message.payee.idPhoto',
          type: 'upload_fixed_image',
          defValue: '',
          isShowStar: true,
          uploadRange: [
            {
              uploadFileList: [],
              fileSize: 2,
              limit: 1,
              isFile: false,
              fileTypeRule: 'image/png,image/jpg,image/jpeg',
              fileTypeRuleTips: 'message.payee.payeeFileTip',
              fileLongDisplayTips: 'message.payee.frontPhoto',
            },
            {
              uploadFileList: [],
              fileSize: 2,
              limit: 1,
              isFile: false,
              fileTypeRule: 'image/png,image/jpg,image/jpeg',
              fileTypeRuleTips: 'message.payee.payeeFileTip',
              fileLongDisplayTips: 'message.payee.reversePhoto',
            },
          ],
        },
        {
          key: 'idNumber',
          name: 'message.payee.identificationNumber',
          type: 'input',
          defValue: '',
          isShowStar: true,
          rule: [REQUIRED, STRING_LENGTH_256, ID_CARD],
        },
        {
          type: 'continuity_select_input',
          subCondition: [
            {
              key: 'name',
              name: 'message.transaction.name',
              type: 'input',
              defValue: '',
              isShowStar: true,
              rule: [REQUIRED, STRING_LENGTH_256],
            },
            {
              key: 'spell',
              name: 'message.payee.namePinyin',
              type: 'input',
              defValue: '',
              isShowStar: true,
              rule: [REQUIRED, STRING_LENGTH_256],
            },
          ],
        },
        {
          type: 'continuity_select_input',
          subCondition: [
            {
              key: 'gender',
              name: 'message.payee.gender',
              type: 'select',
              defValue: '',
              isInternationalization: true,
              isShowStar: true,
              rule: [REQUIRED, STRING_LENGTH_256],
              options: GenderStatus,
            },
            {
              key: 'birthDate',
              name: 'message.payee.birthday',
              type: 'date',
              defValue: '',
              isShowStar: true,
              rule: [REQUIRED, STRING_LENGTH_256],
            },
          ],
        },
        {
          type: 'continuity_select_input',
          subCondition: [
            {
              key: 'effectiveDate',
              name: 'message.payee.effectiveDate',
              type: 'date',
              defValue: '',
              isShowStar: true,
              rule: [REQUIRED, STRING_LENGTH_256],
            },
            {
              key: 'expireDate',
              name: 'message.payee.expireDate',
              type: 'date',
              defValue: '',
              isShowStar: true,
              rule: [REQUIRED, STRING_LENGTH_256],
              pickerOptions: {
                shortcuts: [
                  {
                    text: this.$t('message.payee.longEffectiveness'),
                    onClick(picker) {
                      picker.$emit('pick', new Date('9999-12-31'));
                    },
                  },
                ],
              },
            },
          ],
        },
        {
          key: 'nationality',
          name: 'message.payee.nationality',
          type: 'select',
          defValue: 'CHN',
          isShowStar: true,
          isDisabled: true,
          rule: [REQUIRED, STRING_LENGTH_256],
          options: { label: '中国', isInternationalization: true, value: 'CHN' },
        },
        {
          key: 'profession',
          name: 'message.payee.occupation',
          type: 'select',
          defValue: '',
          isShowStar: true,
          isInternationalization: true,
          rule: [REQUIRED, STRING_LENGTH_256],
          options: ProfessionStatus,
        },
        {
          key: 'address',
          name: 'message.transaction.address',
          type: 'input',
          defValue: '',
          isShowStar: true,
          rule: [REQUIRED, STRING_LENGTH_256],
        },
      ],
      bankInfoCondition: [
        {
          type: 'continuity_select_input',
          subCondition: [
            {
              key: 'province',
              name: 'message.payee.bankAddress',
              type: 'select',
              placeholder: 'message.payee.selectProvince',
              isShowStar: true,
              rule: [REQUIRED],
              options: GetProvinceList(),
              changeValue: (event) => {
                this.onProvinceChange(event);
              },
            },
            {
              key: 'city',
              name: ' ',
              type: 'select',
              placeholder: 'message.payee.selectCity',
              isDisabled: true,
              rule: [REQUIRED],
              changeValue: (event) => {
                this.onCityChange(event);
              },
            },
          ],
        },
        {
          key: 'bankName',
          name: 'message.payee.bankName',
          type: 'select',
          defValue: '',
          isShowStar: true,
          isDisabled: true,
          rule: [REQUIRED],
          changeValue: (event) => {
            this.onBankNameChange(event);
          },
        },
        {
          key: 'bankAccount',
          name: 'message.payee.bankAccount',
          type: 'select',
          defValue: '',
          isShowStar: true,
          isDisabled: true,
          rule: [REQUIRED, STRING_LENGTH_256],
        },
        {
          key: 'cardNumber',
          name: 'message.payee.cardNumber',
          type: 'input',
          defValue: '',
          isShowStar: true,
          rule: [REQUIRED, STRING_LENGTH_256, AllNumber, CARD_LENGTH],
        },
        {
          key: 'phone',
          name: 'message.payee.mobilePhone',
          type: 'input',
          defValue: '',
          isShowStar: true,
          rule: [REQUIRED, STRING_LENGTH_256, AllNumber],
        },
      ],
    };
  },
  methods: {
    onSubmit() {
      if (!this.isRead) {
        ShowWarningMessage(this.$t('message.payee.customerAgreementTips'));
        return;
      }
      this.$refs.basicInfoForm.toFormValid(form => {
        if (form.effectiveDate > form.expireDate) {
          ShowErrorMessage(this.$t('message.payee.illegalValidityTips'));
          return;
        }
        if (this.basicInfoCondition[0].uploadRange[0].uploadFileList.length === 0) {
          ShowErrorMessage(this.$t('message.payee.frontPhotoTips'));
          return;
        }
        if (this.basicInfoCondition[0].uploadRange[1].uploadFileList.length === 0) {
          ShowErrorMessage(this.$t('message.payee.reversePhotoTips'));
          return;
        }
        const params = shallowClones(form);
        params.idCardFront = this.basicInfoCondition[0].uploadRange[0].uploadFileList[0].originUrl;
        params.idCardReverse = this.basicInfoCondition[0].uploadRange[1].uploadFileList[0].originUrl;
        params.birthDate = formatDateDisplay(params.birthDate, 'yyyy-MM-dd');
        params.effectiveDate = formatDateDisplay(params.effectiveDate, 'yyyy-MM-dd');
        params.expireDate = formatDateDisplay(params.expireDate, 'yyyy-MM-dd');
        params.credentialsType = 'ID_CARD';
        delete params.upload_image;
        this.httpPayeeVerify(params);
      });
    },
    onCancel() {
      window.history.back();
    },
    httpPayeeVerify(basicInfoParams) {
      let object = {};
      if (basicInfoParams) {
        object = Object.assign(object, basicInfoParams);
      }
      this.$refs.bankInfoForm.toFormValid(params => {
        object = Object.assign(object, params);
        const bankAccountItem = this.bankAccountList.find(item => object.bankAccount === item.id);
        object.bankName = bankAccountItem.bankName;
        object.jointNum = bankAccountItem.jointNum;
        object.bankAccount = bankAccountItem.bankBranchName;
        this.isLoading = true;
        if (this.infoData && this.infoData.status && this.infoData.status !== 'N') {
          object.type = '1';
          const submitFunction = this.infoData.status === 'S' ? this.postAddPayeeVerify : this.postUpdatePayeeVerify;
          if (this.cardId) {
            object.cardInfoId = this.cardId;
          }
          submitFunction(object).then(data => {
            window.history.back();
            ShowSuccessMessage(this.$t('message.submitSuccessfulTip'));
            this.isLoading = false;
          }).catch(() => {
            this.isLoading = false;
          });
        } else {
          object.type = '0';
          this.postAddPayeeVerify(object).then(data => {
            object.type = '1';
            this.postAddPayeeVerify(object).then(data => {
              window.history.back();
              ShowSuccessMessage(this.$t('message.submitSuccessfulTip'));
              this.isLoading = false;
            }).catch(() => {
              this.isLoading = false;
            });
          }).catch(() => {
            this.isLoading = false;
          });
        }
      });
    },
    openAgreement() {
      this.$refs.agreement.open();
    },
    onCustomerAgree() {
      this.isRead = true;
    },
    onProvinceChange(event) {
      if (event) {
        if (this.$refs.bankInfoForm.getItemValue('city')) {
          this.$refs.bankInfoForm.setItemValue('city', '');
        }
        if (this.$refs.bankInfoForm.getItemValue('bankName')) {
          this.$refs.bankInfoForm.setItemValue('bankName', '');
        }
        if (this.$refs.bankInfoForm.getItemValue('bankAccount')) {
          this.$refs.bankInfoForm.setItemValue('bankAccount', '');
        }
        this.$refs.bankInfoForm.setItemDisabled('city', false);
        this.$refs.bankInfoForm.setItemDisabled('bankName', true);
        this.$refs.bankInfoForm.setItemDisabled('bankAccount', true);
        this.$refs.bankInfoForm.setValidationSelectOptions('city', GetCityList(event));
      } else {
        this.$refs.bankInfoForm.setItemValue('city', '');
        this.$refs.bankInfoForm.setItemValue('bankName', '');
        this.$refs.bankInfoForm.setItemValue('bankAccount', '');
        this.$refs.bankInfoForm.setItemDisabled('city', true);
        this.$refs.bankInfoForm.setItemDisabled('bankAccount', true);
        this.$refs.bankInfoForm.setItemDisabled('bankName', true);
      }
    },
    onCityChange(event, callBack) {
      if (event) {
        if (this.$refs.bankInfoForm.getItemValue('bankName')) {
          this.$refs.bankInfoForm.setItemValue('bankName', '');
        }
        if (this.$refs.bankInfoForm.getItemValue('bankAccount')) {
          this.$refs.bankInfoForm.setItemValue('bankAccount', '');
        }
        this.$refs.bankInfoForm.setItemDisabled('bankAccount', true);
        this.getBankList({ cityNum: event }).then(data => {
          this.$refs.bankInfoForm.setValidationSelectOptions('bankName', initSelectedData(data, 'bankName', 'bankInfo', false));
          this.$refs.bankInfoForm.setItemDisabled('bankName', false);
          if (callBack) {
            callBack(data);
          }
        });
      } else {
        this.$refs.bankInfoForm.setItemValue('bankName', '');
        this.$refs.bankInfoForm.setItemValue('bankAccount', '');
        this.$refs.bankInfoForm.setItemDisabled('bankAccount', true);
        this.$refs.bankInfoForm.setItemDisabled('bankName', true);
      }
    },
    onBankNameChange(event) {
      if (event) {
        if (this.$refs.bankInfoForm.getItemValue('bankAccount')) {
          this.$refs.bankInfoForm.setItemValue('bankAccount', '');
        }
        this.$refs.bankInfoForm.setItemDisabled('bankAccount', false);
        this.bankAccountList = event;
        this.$refs.bankInfoForm.setValidationSelectOptions('bankAccount', initSelectedData(event, 'bankBranchName', 'id', false));
      } else {
        this.$refs.bankInfoForm.setItemValue('bankAccount', '');
        this.$refs.bankInfoForm.setItemDisabled('bankAccount', true);
      }
    },
    initPayeeInfo(res) {
      this.infoData = res;
      if (this.infoData && this.infoData.idNumber) {
        this.$refs.basicInfoForm.assignmentFormValue(res);
        if (res.idCardFront) {
          this.getImageFile({ url: res.idCardFront }).then(data => {
            this.basicInfoCondition[0].uploadRange[0].uploadFileList.push(
              {
                name: this.$t('message.payee.frontPhoto'),
                uid: '' + Math.random().toString(36).slice(-9),
                url: data,
              },
            );
          });
        }
        if (res.idCardReverse) {
          this.getImageFile({ url: res.idCardReverse }).then(data => {
            this.basicInfoCondition[0].uploadRange[1].uploadFileList.push(
              {
                name: this.$t('message.payee.reversePhoto'),
                uid: '' + Math.random().toString(36).slice(-7),
                url: data,
              },
            );
          });
        }
        if (this.infoData.status === 'S') {
          this.$refs.basicInfoForm.setItemDisabled('', true, true);
        } else {
          this.basicInfoCondition[0].uploadRange[0].isDisplayIcon = true;
          this.basicInfoCondition[0].uploadRange[1].isDisplayIcon = true;
        }
      }
    },
  },
  mounted() {
    this.basicInfoCondition[0].uploadRange[0].fileUploadFunc = this.uploadCardUpload;
    this.basicInfoCondition[0].uploadRange[0].fileUploadDisplayFunc = this.getImageFile;
    this.basicInfoCondition[0].uploadRange[1].fileUploadFunc = this.uploadCardUpload;
    this.basicInfoCondition[0].uploadRange[1].fileUploadDisplayFunc = this.getImageFile;
    this.cardId = this.$route.query.cardInfoId;
    this.getPayeeVerifyInfo().then((res) => {
      this.initPayeeInfo(res);
      if (this.cardId) {
        this.getPayeeInfo({ cardInfoId: this.cardId }).then((data) => {
          let bankAccountId = '';
          this.onProvinceChange(data.province);
          this.onCityChange(data.city, event => {
            if (event) {
              const array = event.find(item => data.bankName === item.bankName).bankInfo;
              bankAccountId = array.find(item => data.bankAccount === item.bankBranchName).id;
              this.onBankNameChange(array);
              this.$refs.bankInfoForm.assignmentFormValue(data);
              this.$refs.bankInfoForm.setItemValue('bankAccount', bankAccountId);
            }
          });
          this.$refs.bankInfoForm.setItemDisabled('', false, true);
        }).catch(() => {
        });
      }
    }).catch(() => {
    });
  },
};
</script>
<style lang="scss">
@import '@/styles/variables.scss';
.payee-layout {
  display: flex;
  flex-direction: row;

  .payee-item {
    width: 420px;
  }

  .payee-left-margin {
    margin-right: 20%;
  }

  .payee-agreement {
    font-size: 14px;
    font-weight: 400;
    color: $color-text-primary;
    line-height: 20px;
  }
}
</style>
